/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { baseUrl, getToken } from 'Api';
import { AppContext } from 'Context';
import MembershipCheckout from './MembershipCheckout';
import MemberView from './MemberView';
import MembershipNotice from './MembershipNotice';
import useMembership from './useMembership';
import styles from './index.module.css';

// Affiliate membership flow (this is the /affiliate page):
//   backend decides member vs. not (useMembership) and affiliate status
//   (affiliateDetails → apiResponse) combine to pick the surface:
//     active affiliate            → management surface (MemberView), with a
//                                    buy/reactivate gate when not a member
//     non-active affiliate + none → enroll landing → plans → checkout
//     any membership record while
//       affiliate is pending/     → blocked (contact admin)
//       rejected

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const POPULAR_CODE = 'gold';
const PLAN_TAGS = { silver: 'Most Popular', gold: 'Best Value' };
const SUPPORT_EMAIL = 'support@projectorstream.com';
const INACTIVE_STATUSES = ['canceled', 'expired', 'past_due', 'unpaid', 'paused'];

const formatPrice = (cents) => `$${Math.round(cents / 100).toLocaleString('en-US')}`;

// affiliateDetails returns `data` as an array of records (approved affiliate)
// or an object carrying manager_status (pending / rejected / none). Normalize
// both shapes to one of: 'active' | 'pending' | 'rejected' | 'none'.
const deriveAffiliateStatus = (apiResponse) => {
  const d = apiResponse?.data;
  const record = Array.isArray(d) ? d[0] : d;
  const managerStatus = record?.manager_status;
  if (managerStatus === 'Active' || record?.affiliateCode) return 'active';
  if (managerStatus === 'Pending') return 'pending';
  if (managerStatus === 'Rejected') return 'rejected';
  if (Array.isArray(d) && d.length > 0) return 'active'; // record present, status implied active
  return 'none';
};

// Pull co-branding fields off the affiliate record. affiliateDetails returns
// them nested under `configs` — BrandLogo comes back as a full presigned
// (CloudFront) URL for display; BrandName is the label. `folder` scopes new
// logo uploads per manager (affiliate code).
const deriveBrand = (apiResponse) => {
  const d = apiResponse?.data;
  const record = Array.isArray(d) ? d[0] : d;
  const configs = record?.configs || {};
  const rawLogo = configs.BrandLogo || '';
  const isUrl = /^https?:\/\//i.test(rawLogo);
  return {
    name: configs.BrandName || '',
    logoUrl: isUrl ? rawLogo : '',
    logoKey: isUrl ? '' : rawLogo, // raw key to resend when the logo is unchanged
    folder: record?.affiliateCode || record?.manager_id || '',
  };
};

// Anomaly guard (should not normally happen): a membership record while the
// affiliate request is still pending / rejected.
const AffiliateBlocked = ({ status }) => {
  const pending = status === 'pending';
  return (
    <div className={styles.noticeWrap}>
      <div className={`${styles.noticeIcon} ${styles.noticeIconWarn}`}>!</div>
      <h1 className={styles.noticeTitle}>
        {pending ? 'Affiliate approval pending' : 'Affiliate access blocked'}
      </h1>
      <p className={styles.noticeText}>
        {pending
          ? 'Your affiliate request is still pending review, so the membership page isn’t available yet.'
          : 'Your affiliate request has been blocked, so the membership page isn’t available.'}
        {' '}
        Please contact the administrator at
        {' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.noticeLink}>{SUPPORT_EMAIL}</a>
        .
      </p>
    </div>
  );
};

function MembershipDemo() {
  const {
    loading: membershipLoading, isMember, status, membership, refetch,
  } = useMembership();
  const {
    apiResponse, fetchData, fetchBrandConfig, isLoading: affiliateLoading,
  } = useContext(AppContext);
  // Refresh both the affiliate record (this page) and the global brand logo
  // (app headers) after the brand is saved.
  const handleBrandSaved = () => { fetchData?.(); fetchBrandConfig?.(); };

  // Load affiliate status once so we can combine it with membership state.
  useEffect(() => {
    fetchData?.();
  }, []);

  const [enrolled, setEnrolled] = useState(false);
  const [billing, setBilling] = useState('annual'); // global toggle: 'monthly' | 'annual'
  const [cardBilling, setCardBilling] = useState({}); // per-card cycle, keyed by plan id
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null); // { plan, pricing }
  // Set when Stripe redirects back with a session/subscription id to confirm.
  const [resume, setResume] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get('session_id');
    const sub = p.get('subscription_id');
    return s || sub ? { sessionId: s, subscriptionId: sub } : null;
  });
  // Hosted-checkout cancel_url returns here with ?checkout=cancelled.
  const [canceled, setCanceled] = useState(
    () => new URLSearchParams(window.location.search).get('checkout') === 'cancelled',
  );
  // User clicked Re-subscribe from an ended/past-due notice → jump to plans.
  const [resubscribe, setResubscribe] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = getToken();
      const { data } = await axios.get(
        `${baseUrl}/v4/membership-plans`,
        token ? { params: { token } } : undefined,
      );
      if (data?.success && Array.isArray(data.data)) setPlans(data.data);
      else setError(true);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = () => {
    setEnrolled(true);
    if (!plans.length) fetchPlans();
  };

  const clearUrl = () => window.history.replaceState({}, '', window.location.pathname);
  // Purchase confirmed → let the backend be the source of truth for the result.
  const handlePaySuccess = () => {
    clearUrl();
    setResume(null);
    setSelected(null);
    setEnrolled(false);
    setResubscribe(false);
    refetch();
    fetchData?.(); // affiliate request may have been flipped to Active on purchase
  };
  // Return to the plan grid (also recovers state after a full-page redirect).
  const goToPlans = () => {
    clearUrl();
    setResume(null);
    setCanceled(false);
    setSelected(null);
    setEnrolled(true);
    if (!plans.length) fetchPlans();
  };

  const getPricingBy = (plan, cycle) => plan.pricing
    ?.find((p) => p.billing_title === (cycle === 'monthly' ? 'Monthly' : 'Annual'))
    || plan.pricing?.find((p) => p.billing_title === 'Annual')
    || plan.pricing?.[0];
  const getPricing = (plan) => getPricingBy(plan, billing);

  // ---- Returning from a canceled hosted checkout ----
  if (canceled) {
    return (
      <div className={styles.wrap}>
        <MembershipCheckout canceled onBack={goToPlans} />
      </div>
    );
  }

  // ---- Returning from a Stripe redirect → confirm the payment ----
  if (resume) {
    return (
      <div className={styles.wrap}>
        <MembershipCheckout resume={resume} onBack={goToPlans} onSuccess={handlePaySuccess} />
      </div>
    );
  }

  // ---- Resolving membership + affiliate status (don't flash the plan grid) ----
  if (membershipLoading || (affiliateLoading && apiResponse === undefined)) {
    return (
      <div className={styles.wrap}>
        <div className={styles.membLoading}><div className={styles.coSpinner} /></div>
      </div>
    );
  }

  const affiliate = deriveAffiliateStatus(apiResponse);
  const brand = deriveBrand(apiResponse);
  const hasInactiveRecord = !!membership && INACTIVE_STATUSES.includes(status);
  const hasMembershipRecord = isMember || hasInactiveRecord;

  // ---- Payment step (after choosing a plan price) — works for every path ----
  if (selected) {
    return (
      <div className={styles.wrap}>
        <MembershipCheckout
          plan={selected.plan}
          pricing={selected.pricing}
          onBack={goToPlans}
          onSuccess={handlePaySuccess}
        />
      </div>
    );
  }

  // ---- Anomaly guard: membership record while affiliate is pending/rejected ----
  if (hasMembershipRecord && (affiliate === 'pending' || affiliate === 'rejected')) {
    return (
      <div className={styles.wrap}>
        <AffiliateBlocked status={affiliate} />
      </div>
    );
  }

  // ---- Active affiliate → management surface, gated by membership ----
  // Case 2 (no membership) and Case 4 (expired) both show the affiliate/user
  // management UI with a buy/reactivate gate; Case 3 (member) shows it normally.
  if (affiliate === 'active' && !enrolled && !resubscribe) {
    const membershipState = isMember ? 'active' : (hasInactiveRecord ? 'expired' : 'none');
    const onGetMembership = () => {
      if (membershipState === 'expired') { setResubscribe(true); }
      goToPlans();
    };
    return (
      <div className={styles.wrap}>
        <MemberView
          membership={membership}
          onChanged={refetch}
          affiliateActive
          membershipState={membershipState}
          onGetMembership={onGetMembership}
          brandName={brand.name}
          brandLogoUrl={brand.logoUrl}
          brandLogoKey={brand.logoKey}
          brandFolder={brand.folder}
          onBrandSaved={handleBrandSaved}
        />
      </div>
    );
  }

  // ---- Member (non-affiliate path, e.g. affiliate still resolving) ----
  if (isMember && !enrolled && !resubscribe) {
    return (
      <div className={styles.wrap}>
        <MemberView membership={membership} onChanged={refetch} />
      </div>
    );
  }

  // ---- Not active, but the backend still knows the plan ----
  // canceled/expired → "ended" + Re-subscribe; past_due/unpaid/paused → update card.
  if (!resubscribe && hasInactiveRecord && !enrolled) {
    return (
      <div className={styles.wrap}>
        <MembershipNotice
          status={status}
          membership={membership}
          onResubscribe={() => { setResubscribe(true); goToPlans(); }}
        />
      </div>
    );
  }

  // ---- Enroll landing ----
  if (!enrolled) {
    return (
      <div className={styles.wrap}>
        <div className={styles.hero}>
          <span className={styles.badge}>Affiliate Program</span>
          <h1 className={styles.heroTitle}>Earn as you grow</h1>
          <p className={styles.heroSub}>
            Join the Projector Affiliate Program to unlock member pricing, included subscriptions,
            and recurring discounts. Pick a membership that fits you and start creating
            accounts for your network
          </p>
          <div className={styles.valueProps}>
            <div className={styles.valueProp}>
              <span className={styles.valueIcon}><CheckIcon /></span>
              <span>Included Annual Subscriptions</span>
            </div>
            <div className={styles.valueProp}>
              <span className={styles.valueIcon}><CheckIcon /></span>
              <span>Exclusive member-only discounts</span>
            </div>
            <div className={styles.valueProp}>
              <span className={styles.valueIcon}><CheckIcon /></span>
              <span>Create &amp; manage your own users</span>
            </div>
            <div className={styles.valueProp}>
              <span className={styles.valueIcon}><CheckIcon /></span>
              <span>Lifetime savings that scale with you</span>
            </div>
          </div>
          <button type="button" className={styles.enrollBtn} onClick={handleEnroll}>Enroll Now</button>
          <p className={styles.heroFoot}>Cancel anytime · Instant access after purchase</p>
        </div>
      </div>
    );
  }

  // ---- Plan cards ----
  const planHasBilling = (plan, title) => (plan.pricing || []).some((p) => p.billing_title === title);
  const planHasBoth = (plan) => planHasBilling(plan, 'Monthly') && planHasBilling(plan, 'Annual');
  const validPlans = plans.filter((plan) => getPricing(plan));
  const dualPlans = validPlans.filter(planHasBoth);
  // 2+ plans support both cycles -> keep the global toggle. Exactly one such
  // plan -> move the switch into that card. Grid centers only for a lone card.
  const showGlobalToggle = !loading && !error && dualPlans.length >= 2;
  const singlePlanMode = !loading && !error && validPlans.length === 1;

  const billingToggle = (className) => (
    <div className={className}>
      <button
        type="button"
        className={`${styles.toggleBtn} ${billing === 'monthly' ? styles.toggleActive : ''}`}
        onClick={() => setBilling('monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        className={`${styles.toggleBtn} ${billing === 'annual' ? styles.toggleActive : ''}`}
        onClick={() => setBilling('annual')}
      >
        Annually
      </button>
    </div>
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.plansHead}>
        <h1 className={styles.plansTitle}>Choose your membership</h1>
        <p className={styles.plansSub}>Select a plan to become a Projector affiliate.</p>
        {showGlobalToggle && billingToggle(styles.toggle)}
      </div>

      {loading ? (
        <p className={styles.stateText}>Loading plans...</p>
      ) : error ? (
        <p className={styles.stateText}>Couldn&apos;t load plans. Please try again later.</p>
      ) : (
        <>
          <div className={`${styles.cards} ${singlePlanMode ? styles.cardsSingle : ''}`}>
            {plans.map((plan) => {
              // In-card switch drives this card's cycle only; others use the global toggle.
              const inCardSwitch = !showGlobalToggle && planHasBoth(plan);
              const cycle = inCardSwitch ? (cardBilling[plan.id] || 'annual') : billing;
              const pricing = getPricingBy(plan, cycle);
              if (!pricing) return null;
              const annualOnly = cycle === 'monthly' && pricing.billing_title !== 'Monthly';
              const isPopular = plan.code === POPULAR_CODE;
              const planTag = PLAN_TAGS[plan.code];
              const showcase = pricing.benefits?.showcase || [];
              return (
                <div key={plan.id} className={`${styles.card} ${isPopular ? styles.cardPopular : ''}`}>
                  {planTag && <span className={styles.popularTag}>{planTag}</span>}
                  {inCardSwitch && (
                    <div className={styles.miniSwitch}>
                      <span className={`${styles.miniThumb} ${cycle === 'annual' ? styles.miniThumbAnnual : ''}`} />
                      <button
                        type="button"
                        className={`${styles.miniOpt} ${cycle === 'monthly' ? styles.miniOptActive : ''}`}
                        onClick={() => setCardBilling((prev) => ({ ...prev, [plan.id]: 'monthly' }))}
                      >
                        Monthly
                      </button>
                      <button
                        type="button"
                        className={`${styles.miniOpt} ${cycle === 'annual' ? styles.miniOptActive : ''}`}
                        onClick={() => setCardBilling((prev) => ({ ...prev, [plan.id]: 'annual' }))}
                      >
                        Annual
                      </button>
                    </div>
                  )}
                  <p className={styles.planName}>{plan.name}</p>
                  <div className={styles.priceRow}>
                    <span className={styles.price}>{formatPrice(pricing.price_cents)}</span>
                    <span className={styles.per}>{`/ ${pricing.billing_title}`}</span>
                  </div>
                  {annualOnly && <span className={styles.annualOnly}>Annual billing only</span>}
                  <ul className={styles.benefits}>
                    {showcase.map((b, i) => (
                      <li key={i} className={styles.benefit}>
                        <span className={styles.benefitCheck}><CheckIcon /></span>
                        {b.title}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={`${styles.chooseBtn} ${isPopular ? styles.chooseBtnPrimary : ''}`}
                    onClick={() => setSelected({ plan, pricing })}
                  >
                    {`Choose ${plan.name}`}
                  </button>
                </div>
              );
            })}

            {/* Static Enterprise plan — always last; routes to sales email. */}
            <div className={`${styles.card} ${styles.cardEnterprise}`}>
              <p className={styles.planName}>Enterprise Membership</p>
              <div className={styles.priceRow}>
                <span className={styles.enterprisePrice}>Custom pricing</span>
              </div>
              <ul className={styles.benefits}>
                <li className={styles.benefit}>
                  <span className={styles.benefitCheck}><CheckIcon /></span>
                  Volume user creation &amp; management
                </li>
                <li className={styles.benefit}>
                  <span className={styles.benefitCheck}><CheckIcon /></span>
                  Dedicated support &amp; onboarding
                </li>
                <li className={styles.benefit}>
                  <span className={styles.benefitCheck}><CheckIcon /></span>
                  Tailored pricing for your organization
                </li>
              </ul>
              <a
                className={`${styles.chooseBtn} ${styles.chooseBtnPrimary}`}
                href="mailto:insider@projectorstream.com?subject=Enterprise%20Membership%20Enquiry"
              >
                Contact sales
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MembershipDemo;
